// signals-web.ts — the WEB runtime's derived habit signals.
//
// 🔴 WHY THIS EXISTS: `signals_for_analysis` was a zero stub in web-commands.ts. On web every
// figure came back 0 and the habit list empty, so the billed AI analysis was handed a tracker with
// no habits in it. Native is unaffected (Rust computes it in db.rs).
//
// Every rule below mirrors `src-tauri/src/db.rs::signals_for_analysis` and its helpers. Where the
// two could drift, the Rust wins — it is the shipping surface.
//
// ⚠️ OBSERVATIONAL, NOT MOTIVATIONAL. The Rust facts state what happened ("hasn't been checked in
// for 21 days — its last was …"), never a nudge. Keep that voice: this is someone's own record,
// not a coach.

import type { Habit } from "./store";

const DAY = 86400000;
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const parse = (d: string) => Date.parse(`${d.slice(0, 10)}T00:00:00Z`);

/** Mirror of db.rs::days_between — whole days from a to b (negative if b is earlier). */
const daysBetween = (a: string, b: string) => Math.round((parse(b) - parse(a)) / DAY);
/** Mirror of db.rs::add_days. */
const addDays = (d: string, delta: number) => iso(parse(d) + delta * DAY);

/** Mirror of db.rs::current_streak — counts back from today, or yesterday if today is unlogged. */
function currentStreak(set: Set<string>, today: string): number {
  if (!set.size) return 0;
  let cur = today;
  if (!set.has(cur)) {
    cur = addDays(cur, -1);
    if (!set.has(cur)) return 0;
  }
  let streak = 0;
  while (set.has(cur)) { streak += 1; cur = addDays(cur, -1); }
  return streak;
}

/** Mirror of db.rs::best_streak_and_gap over sorted DISTINCT days. */
function bestStreakAndGap(days: string[]): [number, number] {
  if (!days.length) return [0, 0];
  let best = 1, run = 1, gap = 0;
  for (let i = 1; i < days.length; i++) {
    const diff = daysBetween(days[i - 1], days[i]);
    if (diff === 1) { run += 1; if (run > best) best = run; }
    else if (diff > 1) { if (diff - 1 > gap) gap = diff - 1; run = 1; }
  }
  return [best, gap];
}

/** Mirror of db.rs::expected_for — adhoc has no schedule, so adherence is not meaningful. */
const expectedFor = (cadence: string, windowDays: number) =>
  cadence === "daily" ? windowDays : cadence === "weekly" ? windowDays / 7 : 0;

/** Mirror of db.rs::cadence_actual_label. */
function cadenceActualLabel(perWeek: number, daysSinceLast: number): string {
  if (daysSinceLast > 30) return "dormant";
  if (perWeek >= 6) return "~daily";
  if (perWeek >= 2.5) return "~3x/week";
  if (perWeek >= 0.6) return "~weekly";
  return "sporadic";
}

const dormantCountTone = (dormant: number, total: number) =>
  total > 0 && dormant * 2 >= total ? "watch" : "neutral";

export interface HabitWithDates { habit: Habit; dates: string[] }

/** Mirror of db.rs::signals_for_analysis. */
/** One tallied miss reason — mirrors db.rs::ReasonTally. */
export interface ReasonTallyWeb { habit: string; reason: string; count: number; of_answered: number }

export function computeSignalsWeb(
  input: HabitWithDates[],
  todayStr: string,
  reasons: ReasonTallyWeb[] = [],
) {
  const today = todayStr.slice(0, 10);
  const monthFirst = `${today.slice(0, 7)}-01`;
  const lastMonthLast = addDays(monthFirst, -1);
  const lastMonthFirst = `${lastMonthLast.slice(0, 7)}-01`;
  const last7From = addDays(today, -6);
  const prev7From = addDays(today, -13);
  const prev7To = addDays(today, -7);
  const cutoff30 = addDays(today, -29);

  let totalAll = 0;
  const outHabits = input.map(({ habit: h, dates: raw }) => {
    // Distinct, sorted — the Rust builds a BTreeSet for exactly this.
    const days = [...new Set(raw.map((d) => d.slice(0, 10)))].sort();
    const daySet = new Set(days);
    const total = days.length;
    totalAll += total;

    const first = days[0] ?? null;
    const last = days.length ? days[days.length - 1] : null;
    const createdDay = (h.created_at ?? "").slice(0, 10);

    const ageDays = createdDay ? Math.max(0, daysBetween(createdDay, today)) : 0;
    const spanDays = first && last ? Math.max(0, daysBetween(first, last)) + 1 : 0;
    // A backfilled habit older than its first log still reads sensibly.
    const livedDays = Math.max(1, ageDays, spanDays);
    const daysSinceLast = last ? Math.max(0, daysBetween(last, today)) : -1;

    const cur = currentStreak(daySet, today);
    const [best, longestGap] = bestStreakAndGap(days);

    const countIn = (from: string, to: string) => days.filter((d) => d >= from && d <= to).length;
    const active30 = countIn(cutoff30, today);
    const thisMonth = countIn(monthFirst, today);
    const lastMonth = countIn(lastMonthFirst, lastMonthLast);
    const last7 = countIn(last7From, today);
    const prev7 = countIn(prev7From, prev7To);

    const perWeek = total / Math.max(1 / 7, livedDays / 7);
    const cadenceActual = cadenceActualLabel(perWeek, Math.max(0, daysSinceLast));

    const expected30 = expectedFor(h.cadence_target, 30);
    // -1 means N/A (adhoc has no schedule) — the brain is told, not left to guess.
    const adherence30 = expected30 > 0
      ? Math.min(100, Math.max(0, Math.round((active30 / expected30) * 100)))
      : -1;

    const cadenceMismatch = h.cadence_target === "daily"
      ? perWeek < 5 && daysSinceLast <= 30 && total >= 3
      : h.cadence_target === "weekly"
        ? perWeek >= 3 && total >= 3
        : false;

    const domToday = Number(today.slice(8, 10)) || 30;
    const lastMonthLen = daysBetween(lastMonthFirst, lastMonthLast) + 1;
    const period = (label: string, count: number, winDays: number) => {
      const exp = expectedFor(h.cadence_target, winDays);
      return {
        label, checkins: count,
        adherence_pct: exp > 0 ? Math.min(100, Math.max(0, Math.round((count / exp) * 100))) : -1,
      };
    };
    const periods = [
      period("this_month", thisMonth, domToday),
      period("last_month", lastMonth, lastMonthLen),
      period("last_7d", last7, 7),
      period("prev_7d", prev7, 7),
    ];

    // Normalised by elapsed days so an early-in-the-month read is not unfairly "slipping".
    const tmRate = domToday > 0 ? thisMonth / domToday : 0;
    const lmRate = lastMonthLen > 0 ? lastMonth / lastMonthLen : 0;
    const trend = lastMonth === 0 && thisMonth === 0 ? "flat"
      : lmRate === 0 && tmRate > 0 ? "rising"
      : tmRate > lmRate * 1.2 ? "rising"
      : tmRate < lmRate * 0.8 ? "slipping"
      : "steady";

    const maturity = ageDays < 14 ? "new" : ageDays < 60 ? "establishing" : "established";

    // Mirrors db.rs: on a habit being BROKEN the marks are SLIPS, so adherence is N/A and a run
    // of marks is never a "best". longest_gap_days (the clean run) carries the meaning.
    const isBreak = (h as { polarity?: string }).polarity === "break";
    return {
      name: h.name,
      polarity: isBreak ? "break" : "build",
      cadence_target: h.cadence_target,
      age_days: ageDays,
      maturity,
      total_checkins: total,
      first_logged_on: first,
      last_logged_on: last,
      active_days_30d: active30,
      adherence_30d_pct: isBreak ? -1 : adherence30,
      observed_per_week: Math.round(perWeek * 10) / 10,
      cadence_actual: cadenceActual,
      cadence_mismatch: cadenceMismatch,
      streak: { current: isBreak ? 0 : cur, best: isBreak ? 0 : best, longest_gap_days: longestGap, days_since_last: daysSinceLast, active: isBreak ? false : cur > 0 },
      periods,
      trend,
    };
  });

  const activeStreaks = outHabits.filter((h) => h.streak.active).length;
  const dormantCount = outHabits.filter((h) => h.streak.days_since_last >= 30).length;
  const sumPeriod = (label: string) =>
    outHabits.flatMap((h) => h.periods).filter((p) => p.label === label).reduce((a, p) => a + p.checkins, 0);
  const checkinsLast7 = sumPeriod("last_7d");
  const checkinsPrev7 = sumPeriod("prev_7d");

  return {
    schema: "habit-signals/v2: derived aggregates only (raw check-in dates stay on the device). Streaks, adherence against the cadence the USER set, observed rhythm, per-period counts and trend. headline_facts = pre-ranked, ready-to-say observational sentences already carrying the figures — lead with these, never invent a number. Describe what the record shows; never coach, never prescribe.",
    generated_on: today,
    habit_count: outHabits.length,
    total_checkins_all: totalAll,
    active_streaks: activeStreaks,
    dormant_count: dormantCount,
    checkins_last_7d: checkinsLast7,
    checkins_prev_7d: checkinsPrev7,
    habits: outHabits,
    headline_facts: [
      ...buildHeadlineFacts(outHabits, activeStreaks, dormantCount, checkinsLast7, checkinsPrev7),
      // Same threshold as db.rs: 3+ answers AND more than half of them, or it is noise.
      ...reasons
        .filter((t) => t.count >= 3 && t.count * 2 > t.of_answered)
        .map((t) => ({
          kind: "miss_reason",
          text: `${t.count} of the ${t.of_answered} missed ${t.habit} days you explained were "${t.reason}".`,
          tone: "neutral",
        })),
    ],
    miss_reasons: reasons,
  };
}

// Declared explicitly, not derived with ReturnType<typeof computeSignalsWeb>: that form makes the
// function's return type depend on a helper it calls, which TypeScript rejects as circular
// (TS2456). Same trap as Curio's port.
interface HabitSig {
  name: string;
  cadence_target: string;
  age_days: number;
  maturity: string;
  total_checkins: number;
  first_logged_on: string | null;
  last_logged_on: string | null;
  active_days_30d: number;
  polarity: string;
  adherence_30d_pct: number;
  observed_per_week: number;
  cadence_actual: string;
  cadence_mismatch: boolean;
  streak: { current: number; best: number; longest_gap_days: number; days_since_last: number; active: boolean };
  periods: { label: string; checkins: number; adherence_pct: number }[];
  trend: string;
}

/** Mirror of db.rs::build_headline_facts. Capped at 6, most informative first. */
function buildHeadlineFacts(
  habits: HabitSig[], activeStreaks: number, dormantCount: number, last7: number, prev7: number,
) {
  const facts: { kind: string; text: string; tone: string }[] = [];
  if (!habits.length) return facts;
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
  const maxBy = <T,>(xs: T[], k: (x: T) => number): T | null =>
    xs.length ? xs.reduce((a, b) => (k(b) > k(a) ? b : a)) : null;

  // 1) The longest LIVE streak.
  const top = maxBy(habits.filter((h) => h.streak.active), (h) => h.streak.current);
  if (top && top.streak.current >= 3) {
    const atBest = top.streak.current >= top.streak.best && top.streak.best > 0;
    const text = atBest && habits.length > 1
      ? `"${top.name}" is on a ${top.streak.current}-day streak — your longest run yet for it.`
      : habits.length > 1
        ? `"${top.name}" has your longest active streak at ${top.streak.current} days.`
        : `"${top.name}" is on a ${top.streak.current}-day streak.`;
    facts.push({ kind: "streak", text, tone: "good" });
  }

  // 2) A best-ever run that is no longer running.
  const rec = maxBy(habits, (h) => h.streak.best);
  if (rec && rec.streak.best >= 5 && !rec.streak.active) {
    facts.push({
      kind: "streak",
      text: `Your best-ever run was ${rec.streak.best} days on "${rec.name}"; it has no streak going right now.`,
      tone: "watch",
    });
  }

  // 3) Best adherence against the cadence the USER set.
  const bestAdh = maxBy(habits.filter((h) => h.adherence_30d_pct >= 0 && h.total_checkins >= 3), (h) => h.adherence_30d_pct);
  if (bestAdh && bestAdh.adherence_30d_pct >= 70) {
    facts.push({
      kind: "consistency",
      text: `You've kept "${bestAdh.name}" at ${bestAdh.adherence_30d_pct}% of its ${bestAdh.cadence_target} target over the last 30 days.`,
      tone: "good",
    });
  }

  // 4) Set cadence vs observed rhythm — stated, not judged (weekly done more often reads "good").
  const mm = maxBy(habits.filter((h) => h.cadence_mismatch && h.total_checkins >= 3), (h) => h.total_checkins);
  if (mm) {
    facts.push({
      kind: "cadence",
      text: `You set "${mm.name}" as ${mm.cadence_target}, but you're actually checking in ${mm.cadence_actual.replace("~", "about ")} (${mm.observed_per_week.toFixed(1)}×/week).`,
      tone: mm.cadence_target === "weekly" ? "good" : "watch",
    });
  }

  // 5) Week-over-week across the whole set.
  if (last7 + prev7 >= 4) {
    if (prev7 === 0 && last7 > 0) {
      facts.push({
        kind: "trend",
        text: `You've logged ${last7} check-${plural(last7, "in", "ins")} in the last 7 days after none the week before.`,
        tone: "good",
      });
    } else if (prev7 > 0) {
      const pct = Math.round(((last7 - prev7) / prev7) * 100);
      if (pct >= 20) {
        facts.push({ kind: "trend", text: `Check-ins are up ${pct}% this week — ${last7} vs ${prev7} the week before.`, tone: "good" });
      } else if (pct <= -20) {
        facts.push({ kind: "trend", text: `Check-ins are down ${Math.abs(pct)}% this week — ${last7} vs ${prev7} the week before.`, tone: "watch" });
      }
    }
  }

  // 6) The longest quiet stretch — only when it is a real lull, not noise.
  const g = maxBy(habits.filter((h) => h.streak.longest_gap_days >= 7 && h.streak.longest_gap_days > h.streak.current + 2), (h) => h.streak.longest_gap_days);
  if (g) {
    facts.push({ kind: "gap", text: `The longest you've gone without "${g.name}" is ${g.streak.longest_gap_days} days.`, tone: "neutral" });
  }

  // 7) A habit gone quiet — how long, observationally, with no nudge attached.
  const d = maxBy(habits.filter((h) => h.streak.days_since_last >= 14), (h) => h.streak.days_since_last);
  if (d) {
    facts.push({
      kind: "cadence",
      text: `"${d.name}" hasn't been checked in for ${d.streak.days_since_last} days — its last was ${d.last_logged_on ?? "a while ago"}.`,
      tone: "watch",
    });
  }

  // 8) Month-over-month for the most-active habit.
  const busy = maxBy(habits, (h) => h.total_checkins);
  if (busy) {
    const tm = busy.periods.find((p) => p.label === "this_month")?.checkins ?? 0;
    const lm = busy.periods.find((p) => p.label === "last_month")?.checkins ?? 0;
    if (busy.trend === "rising" && tm > 0) {
      facts.push({
        kind: "trend",
        text: `"${busy.name}" is trending up — ${tm} check-${plural(tm, "in", "ins")} so far this month vs ${lm} all of last month.`,
        tone: "good",
      });
    } else if (busy.trend === "slipping" && lm > 0) {
      facts.push({
        kind: "trend",
        text: `"${busy.name}" is easing off — ${tm} so far this month, on pace below last month's ${lm}.`,
        tone: "watch",
      });
    }
  }

  // 9) Portfolio snapshot — so even a quiet set gets one true fact.
  if (facts.length < 2) {
    const total = habits.reduce((a, h) => a + h.total_checkins, 0);
    if (activeStreaks > 0) {
      facts.push({
        kind: "milestone",
        text: `You're tracking ${habits.length} ${plural(habits.length, "habit", "habits")} with ${activeStreaks} streak${activeStreaks === 1 ? "" : "s"} live and ${total} total check-${plural(total, "in", "ins")} on record.`,
        tone: "good",
      });
    } else if (total > 0) {
      facts.push({
        kind: "milestone",
        text: `You have ${total} total check-${plural(total, "in", "ins")} on record across ${habits.length} ${plural(habits.length, "habit", "habits")}; no streak is live right now.`,
        tone: dormantCountTone(dormantCount, habits.length),
      });
    }
  }

  return facts.slice(0, 6);
}
